import React, { useEffect, useState } from 'react';

// Utilities
import API, { socketPurchases } from '../../../../../utils/api';
import gaUtils from '../../../../GAWrapper/GAWrapper';
import { waitUserCancelCashoutsPending } from '../../../shared/utils/cashouts';

// Store
import usePaymentStore from '../../../shared/store/use-payment-store';

// Hooks
import useConektaCards from '../hooks/use-conekta-cards';
import { useInstanceId } from '../hooks/use-instance-id';
import { useNewConektaCards } from '../hooks/use-new-conekta-cards';

// Components
import Iconify from '../../../../Iconify';
import Text from '../../../../Text/Text';
import { CardButton, CardsGrid } from '../../../shared/components/card-button';
import { ButtonCallToAction } from '../../../shared/components/styled/styled';
import { Switch } from '../../../shared/components/switch/switch';

// Modals
import { showModalError } from '../../../shared/components/modals/modal-error';
import {
  ModalIframe3DSecure,
  showModalIframe3DSecure,
} from '../../../shared/components/modals/modal-iframe-3d-secure';
import { showModalSuccessPayment } from '../../../shared/components/modals/modal-success';
import {
  showModalConfirmDeleteCard,
  ModalConfirmDeleteCard,
} from '../../../shared/components/modals/modal-confirm-delete';
import { Verification } from '../../../shared/components/verification';
import useUserContext from '../../../../../hooks/useUserContext';
import { toastSuccess } from '../../../../Toast'; 
// import iconoAbonar from '../../../shared/assets/icono-abonar.png';
import iconoAbonar from '../../../shared/assets/icono-boton-depositar.png';

// Global error handler para capturar errores de contenedores removidos del DOM
window.addEventListener('error', (event) => {
  const errorMessage = event.message || event.error?.message || ''
  
  if (errorMessage.includes('Detected container element removed from DOM')) {
    event.preventDefault()
    
    console.warn('Container element removed from DOM detected:', {
      message: errorMessage,
      error: event.error,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    })
  }
})

export const CardConekta = props => {
  const { userData } = useUserContext();
  const { amount, isInvalidAmount } = usePaymentStore();
  const instanceId = useInstanceId();
  const { data, isLoading, refetch } = useConektaCards();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [saveCard, setSaveCard] = useState(false);
  const { requestCardToken } = useNewConektaCards({
    isRender: !selectedCard && userData?.user?.identity_verified,
    instanceId,
  });

  const cards = data?.cards || [];

  const checkoutWithSavedCard = async () => {
    setIsSubmitting(true);

    const userCancell = await waitUserCancelCashoutsPending();

    if (userCancell) {
      setIsSubmitting(false);
      return;
    }

    try {
      const data = {
        other_amount: amount,
        card_id: selectedCard.id,
      };

      const response = await API.createTokenizedOrder(data);

      const { success, order, three_ds, error, redirect_url } = response.data;

      if (success && three_ds) {
        // Reload cards
        refetch();
        showModalIframe3DSecure({
          url: `${order?.next_action?.redirect_to_url?.url}?source=embbeded`,
          title: '3D Secure',
        });
      } else if (success && !three_ds) {
        showModalIframe3DSecure({
          url: redirect_url,
          title: 'Confirmación de pago',
        });
      } else {
        showModalError({ errorMessage: error?.details?.[0]?.message || 'Ha ocurrido un error' });
      }
    } catch (error) {
      console.error('error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkoutWithNewCard = async () => {
    setIsSubmitting(true);

    const userCancell = await waitUserCancelCashoutsPending();

    if (userCancell) {
      setIsSubmitting(false);
      return;
    }

    try {
      setIsSubmitting(true);
      const { token, hasError } = await requestCardToken();

      if (token && !hasError) {
        const data = {
          other_amount: amount,
          save_card: saveCard,
          token,
        };

        const response = await API.createTokenizedOrder(data);
        const { success, order, three_ds, error, redirect_url } = response.data;

        refetch();

        if (success && three_ds) {
          showModalIframe3DSecure({
            url: `${order?.next_action?.redirect_to_url?.url}?source=embbeded`,
            title: '3D Secure',
          });
        } else if (success && !three_ds) {
          showModalIframe3DSecure({
            url: redirect_url,
            title: 'Confirmación de pago',
          });
        } else {
          showModalError({ errorMessage: error?.details?.[0]?.message || 'Ha ocurrido un error' });
        }
      }
    } catch (error) {
      console.error('error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const processPayment = async () => {
    if (isSubmitting) return;

    if (selectedCard) {
      await checkoutWithSavedCard();
    } else {
      await checkoutWithNewCard();
    }
  };

  useEffect(() => {
    socketPurchases.on('purchase:completed', data => {
      const transaction = {
        amount: data.amount,
        provider: 'Conekta',
        transactionId: data?.trans_id,
      };

      API.trackPurchaseSuccessFB(transaction);
      gaUtils.trackUserPurchase(transaction);

      showModalIframe3DSecure({
        url: data.status_url,
        title: 'ABONO COMPLETADO',
      });

      showModalSuccessPayment({
        amount: data.amount,
        balance: data.balance,
        trans_id: data.trans_id,
      });

      if (props.dispatch) {
        props.updateUserInfo(props.dispatch);
      } else if (props.updateUserBalance) {
        props.updateUserBalance(data.balance);
      }
    });

    socketPurchases.on('purchase:failed', data => {
      showModalIframe3DSecure({
        url: data.status_url,
        title: 'Error',
      });
    });

    return () => {
      socketPurchases.off('purchase:completed');
      socketPurchases.off('purchase:failed');
    };
  }, []);

  useEffect(() => {
    const ENV_URL = 'https://3ds-pay.conekta.com';
    const ENV_URL_2 = 'https://pay.conekta.com';

    const handler = event => {
      if (event.origin === ENV_URL || event.origin === ENV_URL_2) {
        const { checkout_id, order_id, payment_status, error } = event.data;

        const urlRedirect = event?.data?.order?.urlRedirect;

        if (urlRedirect && payment_status === 'paid') {
          showModalIframe3DSecure({
            url: urlRedirect,
            title: 'Confirmación de pago',
          });
        }
      }
    };

    window.addEventListener('message', handler);

    return () => {
      window.removeEventListener('message', handler);
    };
  }, []);

  const handleDeleteCard = async (e, card) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedCard(null);
    const needDelete = await showModalConfirmDeleteCard({
      card,
    });

    if (needDelete) {
      const response = await API.deleteConektaCard(card.id);
      if (response?.data?.success) {
        refetch();
        toastSuccess('Tarjeta eliminada exitosamente');
      }
    }
  };

  const isNewCard = !selectedCard;

  if (!userData?.user?.identity_verified) {
    return (
      <Verification />
    )
  }

  return (
    <>
      <ModalIframe3DSecure />
      <ModalConfirmDeleteCard />
      <CardsGrid>
        {cards?.map(card => (
          <CardButton
            key={card.id}
            card={card}
            isSelected={selectedCard?.id === card.id}
            onSelectedCard={() => setSelectedCard(card)}
            onDeleteCard={handleDeleteCard}
            selected={selectedCard?.id === card.id}
          />
        ))}
        <CardButton
          card={null}
          isSelected={!selectedCard}
          onSelectedCard={() => setSelectedCard(null)}
        >
          <Iconify icon="ic:outline-add-card" style={{ fontSize: 20 }} />
          <Text size={14} style={{ width: '100px' }}>
            Nueva tarjeta
          </Text>
        </CardButton>
      </CardsGrid>

      <div id={instanceId} style={{ height: isNewCard ? '433px' : '0px' }} />

      {isNewCard && <Switch isSelected={saveCard} onChange={setSaveCard} label="Guardar tarjeta" />}
      <button
        onClick={processPayment}
        className="payment-button"
        disabled={isSubmitting || isInvalidAmount}
        style={{
          padding: '10px 60px',
          marginTop: '15px',
          marginLeft: 'auto',
          marginBottom: '15px',
          marginRight: 'auto',
          display: 'flex',
          gap: '10px',
        }}
      >
        <span>{isSubmitting ? 'Abonando...' : 'Abonar'}</span>
        <img src={iconoAbonar} alt="Abonar" style={{ width: '20px', height: '20px' }} />
      </button>
    </>
  );
};
